/**
 * B2 — Anotar o editar una factura recibida (gasto o bien de inversión).
 *
 * Se puede subir la factura (PDF o foto) y pedir a Toga que la lea: la IA solo PROPONE, la
 * persona revisa y confirma. El fichero se adjunta como justificante al guardar.
 */
import { useRef, useState, type FormEvent } from 'react'
import { createPortal } from 'react-dom'
import { AlertCircle, FileUp, Loader2, Paperclip, Sparkles, X } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import { centsToEurosInput, formatCents, parseEurosToCents } from '../../lib/money'
import {
  EXPENSE_CATEGORIES_WITHOUT_VAT,
  EXPENSE_CATEGORY_LABEL,
  EXPENSE_VAT_RATES,
  type ExpenseCategory,
  type ExpenseDto,
  type ExpenseExtractResponse,
  type ExpenseKind,
} from '@toga/shared'
import { rateLabel } from './common'

const inputCls =
  'w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5 focus:outline-none focus:border-toga-500/50'
const ACCEPT = 'application/pdf,image/png,image/jpeg,image/webp'

interface Props {
  issuerId: string
  expense: ExpenseDto | null
  onClose: () => void
  onSaved: () => void | Promise<void>
}

const apiMessage = (err: unknown, fallback: string): string => {
  if (err instanceof ApiError) {
    const b = err.body as
      | { error?: string; issues?: { fieldErrors?: Record<string, string[]> } }
      | undefined
    const first = b?.issues?.fieldErrors
      ? Object.values(b.issues.fieldErrors).flat().find(Boolean)
      : undefined
    return first ?? b?.error ?? fallback
  }
  return err instanceof Error ? err.message : fallback
}

export default function ExpenseModal({ issuerId, expense, onClose, onSaved }: Props) {
  const editing = expense !== null
  const [kind, setKind] = useState<ExpenseKind>(expense?.kind ?? 'GASTO')
  const [issueDate, setIssueDate] = useState(
    expense?.issueDate.slice(0, 10) ?? new Date().toISOString().slice(0, 10)
  )
  const [supplierName, setSupplierName] = useState(expense?.supplierName ?? '')
  const [supplierNif, setSupplierNif] = useState(expense?.supplierNif ?? '')
  const [invoiceNumber, setInvoiceNumber] = useState(expense?.invoiceNumber ?? '')
  const [category, setCategory] = useState<ExpenseCategory>(expense?.category ?? 'OTROS')
  const [description, setDescription] = useState(expense?.description ?? '')
  const [base, setBase] = useState(expense ? centsToEurosInput(expense.baseCents) : '')
  const [rate, setRate] = useState(expense?.vatRate ?? '0.2100')
  const [vat, setVat] = useState(expense ? centsToEurosInput(expense.vatCents) : '')
  const [vatEdited, setVatEdited] = useState(editing)
  const [deductiblePct, setDeductiblePct] = useState(String(expense?.deductiblePct ?? 100))
  const [vatDeductiblePct, setVatDeductiblePct] = useState(String(expense?.vatDeductiblePct ?? 100))
  const [life, setLife] = useState(expense?.usefulLifeYears ? String(expense.usefulLifeYears) : '')
  const [notes, setNotes] = useState(expense?.notes ?? '')

  const [file, setFile] = useState<File | null>(null)
  const [reading, setReading] = useState(false)
  const [warnings, setWarnings] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedId, setSavedId] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const baseCents = parseEurosToCents(base)
  const vatCents = vatEdited ? parseEurosToCents(vat) : Math.round(baseCents * Number(rate))
  const totalCents = baseCents + vatCents

  /** Al cambiar base o tipo, la cuota se recalcula salvo que se haya tocado a mano. */
  const onBase = (v: string) => {
    setBase(v)
    if (!vatEdited) setVat(centsToEurosInput(Math.round(parseEurosToCents(v) * Number(rate))))
  }
  const onRate = (v: string) => {
    setRate(v)
    if (!vatEdited) setVat(centsToEurosInput(Math.round(baseCents * Number(v))))
  }
  const onCategory = (c: ExpenseCategory) => {
    setCategory(c)
    // Cuotas de autónomos, personal, tributos… normalmente no llevan IVA.
    if (!editing && EXPENSE_CATEGORIES_WITHOUT_VAT.includes(c)) {
      setRate('0.0000')
      setVatEdited(false)
      setVat('')
    }
    if (c === 'EQUIPOS_Y_MOBILIARIO' && kind === 'GASTO' && !editing) setKind('INVERSION')
  }

  const readWithAi = async () => {
    if (!file) return
    setReading(true)
    setError(null)
    setWarnings([])
    try {
      const form = new FormData()
      form.append('file', file)
      const res = await api.upload<ExpenseExtractResponse>('/expenses/extract', form)
      const s = res.suggestion
      if (s.supplierName) setSupplierName(s.supplierName)
      if (s.supplierNif) setSupplierNif(s.supplierNif)
      if (s.invoiceNumber) setInvoiceNumber(s.invoiceNumber)
      if (s.issueDate) setIssueDate(s.issueDate)
      if (s.category) setCategory(s.category)
      if (s.description) setDescription(s.description)
      if (s.vatRate) {
        const known = EXPENSE_VAT_RATES.find((r) => Number(r) === Number(s.vatRate))
        setRate(known ?? s.vatRate)
      }
      if (s.baseCents !== null) setBase(centsToEurosInput(s.baseCents))
      if (s.vatCents !== null) {
        setVat(centsToEurosInput(s.vatCents))
        setVatEdited(true)
      }
      setWarnings(res.warnings)
    } catch (err) {
      const code = err instanceof ApiError ? (err.body as { code?: string } | undefined)?.code : ''
      setError(
        code === 'AI_NOT_CONFIGURED'
          ? 'La lectura con IA no está configurada en este despacho: rellena los datos a mano.'
          : apiMessage(err, 'No se pudo leer la factura')
      )
    } finally {
      setReading(false)
    }
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!supplierName.trim()) return setError('Indica el proveedor')
    if (baseCents <= 0) return setError('Indica la base imponible')
    if (kind === 'INVERSION' && !Number(life)) return setError('Indica la vida útil en años')
    setBusy(true)
    setError(null)
    const payload = {
      kind,
      category,
      issueDate,
      supplierName: supplierName.trim(),
      supplierNif: supplierNif.trim() || null,
      invoiceNumber: invoiceNumber.trim() || null,
      description: description.trim() || null,
      baseCents,
      vatRate: rate,
      vatCents,
      deductiblePct: Number(deductiblePct),
      vatDeductiblePct: Number(vatDeductiblePct),
      usefulLifeYears: kind === 'INVERSION' ? Number(life) : null,
      notes: notes.trim() || null,
    }
    try {
      let id = expense?.id ?? savedId
      if (id) {
        await api.patch(`/expenses/${id}`, payload)
      } else {
        const r = await api.post<{ expense: ExpenseDto }>('/expenses', { ...payload, issuerId })
        id = r.expense.id
        setSavedId(id)
      }
      if (file) {
        const form = new FormData()
        form.append('file', file)
        try {
          await api.upload(`/expenses/${id}/attachment`, form)
        } catch (err) {
          setError(
            `La factura se ha guardado, pero no se pudo adjuntar el justificante: ${apiMessage(err, 'error')}. Vuelve a guardar para reintentarlo.`
          )
          return
        }
      }
      await onSaved()
    } catch (err) {
      setError(apiMessage(err, 'No se pudo guardar'))
    } finally {
      setBusy(false)
    }
  }

  const openAttachment = async () => {
    if (!expense) return
    try {
      const r = await api.get<{ url: string }>(`/expenses/${expense.id}/attachment`)
      window.open(r.url, '_blank', 'noopener')
    } catch (err) {
      setError(apiMessage(err, 'No se pudo abrir el justificante'))
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center px-4 overflow-y-auto py-8"
      onClick={onClose}
    >
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl surface-deep p-5 animate-slideUp space-y-4"
      >
        <header className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold t-primary">
              {editing ? 'Editar factura recibida' : 'Anotar factura recibida'}
            </h3>
            <p className="text-xs t-muted mt-0.5">
              Gasto del despacho o bien de inversión. Alimenta el libro de gastos y los borradores
              del 303 y el 130.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="t-muted hover:t-primary"
            aria-label="Cerrar"
          >
            <X size={16} />
          </button>
        </header>

        {/* Justificante + lectura con IA */}
        <div className="rounded-lg border border-dashed border-base p-3 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPT}
              className="hidden"
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null)
                setWarnings([])
              }}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="px-3 py-1.5 rounded-lg text-xs t-secondary border border-base hover:bg-hover inline-flex items-center gap-1.5"
            >
              <FileUp size={13} /> {file ? 'Cambiar factura' : 'Subir la factura (PDF o foto)'}
            </button>
            {file && (
              <>
                <span className="text-xs t-muted truncate max-w-[16rem]" title={file.name}>
                  {file.name}
                </span>
                <button
                  type="button"
                  disabled={reading}
                  onClick={() => void readWithAi()}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-gradient-to-r from-toga-600 to-toga-700 hover:shadow-glow-toga inline-flex items-center gap-1.5 disabled:opacity-50"
                >
                  {reading ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <Sparkles size={13} />
                  )}
                  Leer con IA
                </button>
              </>
            )}
            {!file && expense?.hasAttachment && (
              <button
                type="button"
                onClick={() => void openAttachment()}
                className="text-xs text-toga-300 hover:underline inline-flex items-center gap-1"
              >
                <Paperclip size={12} /> Ver justificante ({expense.attachmentName})
              </button>
            )}
          </div>
          <p className="text-[11px] t-faint">
            La IA solo propone los datos; revísalos antes de guardar. El fichero se guarda como
            justificante de la factura.
          </p>
          {warnings.length > 0 && (
            <ul className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 space-y-0.5 list-disc list-inside">
              {warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex gap-2" role="radiogroup" aria-label="Tipo de partida">
          {(
            [
              ['GASTO', 'Gasto'],
              ['INVERSION', 'Bien de inversión (se amortiza)'],
            ] as const
          ).map(([v, label]) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={kind === v}
              onClick={() => setKind(v)}
              className={[
                'px-3 py-1.5 rounded-lg text-xs border',
                kind === v
                  ? 'bg-toga-500/15 text-toga-200 border-toga-500/40'
                  : 't-muted border-base hover:bg-hover',
              ].join(' ')}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="block sm:col-span-2">
            <span className="text-xs t-muted">Proveedor</span>
            <input
              value={supplierName}
              onChange={(e) => setSupplierName(e.target.value)}
              maxLength={200}
              className={inputCls}
              required
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">NIF del proveedor</span>
            <input
              value={supplierNif}
              onChange={(e) => setSupplierNif(e.target.value)}
              maxLength={30}
              className={`${inputCls} font-mono`}
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">Fecha de la factura</span>
            <input
              type="date"
              value={issueDate}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setIssueDate(e.target.value)}
              className={inputCls}
              required
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">N.º de factura</span>
            <input
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              maxLength={60}
              className={inputCls}
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">Categoría</span>
            <select
              value={category}
              onChange={(e) => onCategory(e.target.value as ExpenseCategory)}
              className={inputCls}
            >
              {Object.entries(EXPENSE_CATEGORY_LABEL).map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          <label className="block sm:col-span-3">
            <span className="text-xs t-muted">Concepto</span>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              className={inputCls}
            />
          </label>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <label className="block">
            <span className="text-xs t-muted">Base imponible (€)</span>
            <input
              value={base}
              onChange={(e) => onBase(e.target.value)}
              inputMode="decimal"
              placeholder="0,00"
              className={`${inputCls} font-mono`}
              required
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">Tipo de IVA</span>
            <select value={rate} onChange={(e) => onRate(e.target.value)} className={inputCls}>
              {[...new Set<string>([...EXPENSE_VAT_RATES, rate])].map((r) => (
                <option key={r} value={r}>
                  {rateLabel(r)}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs t-muted">Cuota de IVA (€)</span>
            <input
              value={vatEdited ? vat : centsToEurosInput(vatCents)}
              onChange={(e) => {
                setVat(e.target.value)
                setVatEdited(true)
              }}
              inputMode="decimal"
              placeholder="0,00"
              className={`${inputCls} font-mono`}
            />
          </label>
          <div className="block">
            <span className="text-xs t-muted">Total</span>
            <p className="mt-1.5 px-2 py-1.5 text-sm font-mono font-semibold t-primary">
              {formatCents(totalCents)}
            </p>
          </div>
          <label className="block">
            <span className="text-xs t-muted">% deducible en IRPF</span>
            <input
              type="number"
              min={0}
              max={100}
              value={deductiblePct}
              onChange={(e) => setDeductiblePct(e.target.value)}
              className={`${inputCls} font-mono`}
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">% IVA deducible</span>
            <input
              type="number"
              min={0}
              max={100}
              value={vatDeductiblePct}
              onChange={(e) => setVatDeductiblePct(e.target.value)}
              className={`${inputCls} font-mono`}
            />
          </label>
          {kind === 'INVERSION' && (
            <label className="block">
              <span className="text-xs t-muted">Vida útil (años)</span>
              <input
                type="number"
                min={1}
                max={50}
                value={life}
                onChange={(e) => setLife(e.target.value)}
                className={`${inputCls} font-mono`}
                required
              />
            </label>
          )}
        </div>
        <p className="text-[11px] t-faint -mt-2">
          Uso mixto (vehículo, suministros del domicilio…): baja los porcentajes. Tu gestoría te
          dice cuáles aplicar.
          {kind === 'INVERSION' &&
            ' Un bien de inversión no es gasto de golpe: se amortiza en su vida útil (por meses, contando el de adquisición).'}
        </p>

        <label className="block">
          <span className="text-xs t-muted">Notas (opcional)</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            maxLength={1000}
            className={inputCls}
          />
        </label>

        {error && (
          <div
            role="alert"
            className="text-xs rounded-lg px-3 py-2 border text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-start gap-2"
          >
            <AlertCircle size={13} className="mt-0.5 shrink-0" /> <span>{error}</span>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {editing || savedId ? 'Guardar cambios' : 'Anotar factura'}
          </button>
        </div>
      </form>
    </div>,
    document.body
  )
}
